import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Plus, Edit, Trash2, Save, X, Loader2, Settings } from 'lucide-react'
import { useState } from 'react'

const AdminParametres = () => {
  const queryClient = useQueryClient()
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({ cle: '', valeur: '', description: '' })

  const { data, isLoading } = useQuery({
    queryKey: ['admin-parametres'],
    queryFn: async () => {
      const res = await api.get('/parametres/')
      return res.data
    }
  })

  const saveMutation = useMutation({
    mutationFn: (newData) => api.post('/parametres/', newData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-parametres'])
      setIsAdding(false)
      setEditingId(null)
      setFormData({ cle: '', valeur: '', description: '' })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/parametres/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['admin-parametres'])
  })

  const handleEdit = (param) => {
    setEditingId(param.id)
    setFormData({ cle: param.cle, valeur: param.valeur, description: param.description || '' })
  }

  const handleSave = (e) => {
    e.preventDefault()
    saveMutation.mutate(formData)
  }

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-blue" /></div>

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-cidh-blue/10 p-2 rounded-lg text-cidh-blue">
                <Settings size={20} />
            </div>
            <h3 className="text-xl font-bold text-cidh-blue font-montserrat">Configuration du Site</h3>
          </div>
          <button 
            onClick={() => setIsAdding(!isAdding)}
            className="btn-primary flex items-center gap-2 py-2 text-sm"
          >
            {isAdding ? <X size={18} /> : <Plus size={18} />}
            {isAdding ? 'Annuler' : 'Ajouter un paramètre'}
          </button>
        </div>

        {isAdding && (
          <div className="p-6 bg-gray-50 border-b animate-fade-in">
            <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-4 h-min">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Clé (Identifiant)</label>
                <input 
                  type="text"
                  required
                  placeholder="ex: site_email"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-cidh-gold/20 outline-none transition-all"
                  value={formData.cle}
                  onChange={(e) => setFormData({...formData, cle: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Valeur</label>
                <input 
                  type="text"
                  required
                  placeholder="Valeur du paramètre"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-cidh-gold/20 outline-none transition-all"
                  value={formData.valeur}
                  onChange={(e) => setFormData({...formData, valeur: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">Description (Optionnel)</label>
                <div className="flex gap-2">
                  <input 
                    type="text"
                    placeholder="À quoi sert ce paramètre ?"
                    className="flex-grow px-4 py-2 rounded-lg border focus:ring-2 focus:ring-cidh-gold/20 outline-none transition-all"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                  <button 
                    disabled={saveMutation.isPending}
                    type="submit" 
                    className="bg-cidh-blue text-white p-2 rounded-lg hover:bg-cidh-blue/90 disabled:opacity-50"
                  >
                    {saveMutation.isPending ? <Loader2 size={24} className="animate-spin" /> : <Save size={24} />}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-cidh-gray-light text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Clé</th>
                <th className="px-6 py-4">Valeur</th>
                <th className="px-6 py-4">Description</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-400 italic">
                    Aucun paramètre configuré.
                  </td>
                </tr>
              )}
              {data?.map((param) => (
                <tr key={param.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-cidh-blue">
                      {param.cle}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {editingId === param.id ? (
                      <input 
                        className="border rounded px-2 py-1 w-full"
                        value={formData.valeur}
                        onChange={(e) => setFormData({...formData, valeur: e.target.value})}
                      />
                    ) : (
                      <span className="text-sm text-gray-700">{param.valeur}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingId === param.id ? (
                      <input 
                        className="border rounded px-2 py-1 w-full"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                      />
                    ) : (
                      <span className="text-xs text-gray-500">{param.description}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    {editingId === param.id ? (
                      <>
                        <button 
                          onClick={handleSave}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Save size={18} />
                        </button>
                        <button 
                          onClick={() => setEditingId(null)}
                          className="p-2 text-gray-400 hover:bg-gray-50 rounded"
                        >
                          <X size={18} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleEdit(param)}
                          className="p-2 text-cidh-gold hover:bg-gold-50 rounded"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => window.confirm('Supprimer ce paramètre ?') && deleteMutation.mutate(param.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default AdminParametres
